import type { ApiEndpointContract, ApiHttpMethod, ApiSideEffectPolicy } from "./api-contracts";
import { coachingApiContractBlockers } from "./coaching-contract-blockers";

// Source refs are kept repo-relative to the current workspace checkout. The
// backend lives in the sibling monorepo under repo-local-copy; an older audit
// snapshot pointed these at /projects/repos/vjal-vibe-review/... which no longer
// resolves on this machine (audit 2026-06-12 medium).
const monorepoRoot = "/projects/repos/repo-local-copy/vjal-monorepo";

const catalogRef = {
  label: "API Metadata Catalog export",
  path: `${monorepoRoot}/evidence/migrations/phase10-membership-finance-2026-06-06T16-00-18-167Z/api-catalog-db1-current-export.json`
};

const backendRoutes = `${monorepoRoot}/apps/vjal-coaching-api/src/routes`;
const platformAuthRoutes = `${monorepoRoot}/packages/worker-utils/src`;

function sourceRef(file: string, line: number) {
  return { label: `backend ${file}`, path: `${backendRoutes}/${file}`, line };
}

function platformSourceRef(file: string, line: number) {
  return { label: `platform auth lane ${file}`, path: `${platformAuthRoutes}/${file}`, line };
}

function route<const T extends ApiEndpointContract>(contract: T) {
  return contract;
}

function coachingRoute(
  key: string,
  family: string,
  method: ApiHttpMethod,
  pathTemplate: string,
  file: string,
  line: number,
  options: {
    idempotency?: ApiEndpointContract["idempotency"];
    lane?: ApiEndpointContract["lane"];
    confidence?: ApiEndpointContract["confidence"];
    sideEffects?: ApiSideEffectPolicy[];
    roleScopeNotes?: string[];
    notes?: string;
    catalogStableKey?: string;
  } = {}
) {
  return route({
    key,
    family,
    method,
    pathTemplate,
    confidence: options.confidence ?? "source-provisional",
    authMode: family === "platform-events-service" ? "same-origin-proxy" : "platform-session",
    idempotency: options.idempotency ?? (method === "GET" ? "forbidden" : "required"),
    ...(options.lane === undefined ? {} : { lane: options.lane }),
    catalogStableKey: options.catalogStableKey,
    sourceRefs: [catalogRef, sourceRef(file, line)],
    sideEffects: options.sideEffects ?? (method === "GET" ? ["none"] : ["db1-write"]),
    roleScopeNotes: options.roleScopeNotes ?? ["Scope and role authorization are enforced by the Coaching API."],
    notes: options.notes ?? "Source-confirmed route; pending complete API Metadata Catalog annotation."
  });
}

const contracts = [
  coachingRoute("auth.me", "auth-service", "GET", "/auth/me", "coaching-auth-shell-routes.ts", 73, {
    confidence: "catalog-needs-annotation",
    catalogStableKey: "vjal-coaching-api:GET:/api/coaching/auth/me",
    roleScopeNotes: ["Hydrates super_admin, admin, coach, company_admin, and participant profile/scope projection."],
    notes: "Secret-free Coaching profile projection."
  }),
  route({
    key: "auth.requestOtp",
    family: "auth-service",
    method: "POST",
    pathTemplate: "/otp/send",
    lane: "platform-auth",
    authMode: "public",
    idempotency: "optional",
    confidence: "catalog-needs-annotation",
    sourceRefs: [catalogRef, platformSourceRef("otp-routes.ts", 107)],
    sideEffects: ["provider-closed"] as ApiSideEffectPolicy[],
    roleScopeNotes: ["Unauthenticated OTP request never returns product records, roles, tokens, or scope data."],
    notes:
      "Platform auth lane (api-agents.vjal.ai). Coaching-local /otp/send is a removed 410 shell; evidence must not include raw OTP values."
  }),
  route({
    key: "auth.verifyOtp",
    family: "auth-service",
    method: "POST",
    pathTemplate: "/otp/verify",
    lane: "platform-auth",
    authMode: "public",
    idempotency: "optional",
    confidence: "catalog-needs-annotation",
    sourceRefs: [catalogRef, platformSourceRef("otp-routes.ts", 172)],
    sideEffects: ["provider-closed"] as ApiSideEffectPolicy[],
    roleScopeNotes: ["Invalid OTP returns no protected profile or product records; valid OTP sets the platform session cookie."],
    notes:
      "Platform auth lane (api-agents.vjal.ai). Success response is { success: true } plus the vjal_auth cookie; profile hydration is a follow-up coaching /auth/me read."
  }),
  route({
    key: "auth.logout",
    family: "auth-service",
    method: "POST",
    pathTemplate: "/auth/logout",
    lane: "platform-auth",
    authMode: "platform-session",
    idempotency: "optional",
    confidence: "catalog-needs-annotation",
    sourceRefs: [catalogRef, platformSourceRef("auth-routes.ts", 139)],
    sideEffects: ["provider-closed"] as ApiSideEffectPolicy[],
    roleScopeNotes: ["Logout ends the shared platform session; the coaching-local /auth/logout route is a removed 410 shell."],
    notes: "Platform auth lane (api-agents.vjal.ai) owns session teardown."
  }),

  coachingRoute("companies.list", "company-service", "GET", "/companies", "coaching-shell-company-routes.ts", 136),
  coachingRoute("companies.create", "company-service", "POST", "/companies", "coaching-shell-company-routes.ts", 137, {
    roleScopeNotes: ["super_admin/admin global write; coach/company_admin/participant cannot create companies."]
  }),
  coachingRoute("companies.detail", "company-service", "GET", "/companies/:company_id", "coaching-shell-company-routes.ts", 138),
  coachingRoute("companies.update", "company-service", "PATCH", "/companies/:company_id", "coaching-shell-company-routes.ts", 139, {
    roleScopeNotes: ["super_admin/admin global write; company_admin sees assigned company read-only."]
  }),

  coachingRoute("programs.list", "program-service", "GET", "/programs", "coaching-shell-program-routes.ts", 164),
  coachingRoute("programs.create", "program-service", "POST", "/programs", "coaching-shell-program-routes.ts", 165, {
    roleScopeNotes: ["super_admin/admin global write; coach/company_admin/participant cannot create programs."]
  }),
  coachingRoute("programs.detail", "program-service", "GET", "/programs/:program_id", "coaching-shell-program-routes.ts", 166),
  coachingRoute("programs.update", "program-service", "PATCH", "/programs/:program_id", "coaching-shell-program-routes.ts", 167, {
    roleScopeNotes: ["super_admin/admin can update; coach and read-only roles cannot assign/change program coach."]
  }),

  coachingRoute("cohorts.list", "cohort-service", "GET", "/cohorts", "coaching-shell-cohort-routes.ts", 235),
  coachingRoute("cohorts.create", "cohort-service", "POST", "/cohorts", "coaching-shell-cohort-routes.ts", 236, {
    roleScopeNotes: ["super_admin/admin global write; coach only within assigned company/program scope."]
  }),
  coachingRoute("cohorts.detail", "cohort-service", "GET", "/cohorts/:cohort_id", "coaching-shell-cohort-routes.ts", 237),
  coachingRoute("cohorts.update", "cohort-service", "PATCH", "/cohorts/:cohort_id", "coaching-shell-cohort-routes.ts", 238, {
    roleScopeNotes: ["company_admin and participant are read-only; coach cannot reassign coach or cancel series."]
  }),
  coachingRoute("cohorts.complete", "cohort-service", "POST", "/cohorts/:cohort_id/complete", "coaching-shell-cohort-routes.ts", 239, {
    roleScopeNotes: ["Completion requires all 10 sessions done; company_admin and participant denied."]
  }),
  coachingRoute("cohorts.progress", "cohort-service", "GET", "/cohorts/:cohort_id/progress", "coaching-shell-cohort-routes.ts", 240),
  coachingRoute("cohorts.reportReadiness", "cohort-service", "GET", "/cohorts/:cohort_id/report", "coaching-shell-cohort-routes.ts", 241),

  coachingRoute("participants.list", "participant-service", "GET", "/participants", "coaching-shell-session-routes.ts", 37),
  coachingRoute("participants.create", "participant-service", "POST", "/participants", "coaching-shell-session-routes.ts", 38, {
    roleScopeNotes: ["super_admin/admin global write; coach only assigned cohorts; read-only roles denied."]
  }),
  coachingRoute("participants.listForCohort", "participant-service", "GET", "/cohorts/:cohort_id/participants", "coaching-shell-session-routes.ts", 39),
  coachingRoute("participants.attachToCohort", "participant-service", "POST", "/cohorts/:cohort_id/participants", "coaching-shell-session-routes.ts", 40, {
    roleScopeNotes: ["Late participants attach only to future sessions; duplicate membership rejected."]
  }),
  coachingRoute("participants.update", "participant-service", "PATCH", "/cohorts/:cohort_id/participants/:participant_id", "coaching-shell-session-routes.ts", 41, {
    roleScopeNotes: ["Allowed fields only (name/first/last/phone/status); email stays with the identity service; read-only roles denied."]
  }),
  coachingRoute("participants.assignMembership", "participant-service", "POST", "/cohorts/:cohort_id/assign-membership", "coaching-shell-session-routes.ts", 43, {
    roleScopeNotes: ["Membership assignment is scoped; read-only roles denied and representative denial must be tested."]
  }),
  coachingRoute("participants.bulkImport", "participant-service", "POST", "/cohorts/:cohort_id/participants/bulk", "coaching-shell-session-routes.ts", 44, {
    roleScopeNotes: ["Bulk rows must reject invalid emails and duplicates; coach limited to assigned cohorts."]
  }),

  coachingRoute("sessions.calendar", "session-service", "GET", "/calendar", "coaching-shell-session-routes.ts", 57, {
    roleScopeNotes: ["Calendar uses the same org/coach/cohort/participant session scope; from/to date bounds are inclusive."],
    notes: "Handwritten calendar read over the scoped session repository; date range is required and validated."
  }),
  coachingRoute("sessions.list", "session-service", "GET", "/sessions", "coaching-shell-session-routes.ts", 46),
  coachingRoute("sessions.create", "session-service", "POST", "/sessions", "coaching-shell-session-routes.ts", 47, {
    roleScopeNotes: ["Direct session create is write-scoped; read-only roles denied."]
  }),
  coachingRoute("sessions.listForCohort", "session-service", "GET", "/cohorts/:cohort_id/sessions", "coaching-shell-session-routes.ts", 48),
  coachingRoute("sessions.previewSeries", "session-service", "POST", "/cohorts/:cohort_id/sessions/series/preview", "coaching-shell-session-routes.ts", 51, {
    sideEffects: ["readiness-evidence"],
    roleScopeNotes: ["Preview precedes creation; zero-participant cohorts remain blocked in UI."]
  }),
  coachingRoute("sessions.createSeries", "session-service", "POST", "/cohorts/:cohort_id/sessions/series", "coaching-shell-session-routes.ts", 52, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Requires timezone confirmation and idempotency; provider side effects may remain evidence-only."]
  }),
  coachingRoute("sessions.rescheduleFuture", "session-service", "POST", "/cohorts/:cohort_id/sessions/reschedule-future", "coaching-shell-session-routes.ts", 53, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Past sessions unchanged; session identity and Zoom continuity labels must remain visible."]
  }),
  coachingRoute("sessions.detail", "session-service", "GET", "/sessions/:session_id", "coaching-shell-session-routes.ts", 54),
  coachingRoute("sessions.sendInvites", "session-service", "POST", "/sessions/:session_id/send-invites", "coaching-shell-session-routes.ts", 55, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Microsoft 365 delivery may be closed; company_admin/participant see status only."]
  }),
  coachingRoute("sessions.forceResend", "session-service", "POST", "/sessions/:session_id/force-resend", "coaching-shell-session-routes.ts", 61, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Force resend is explicit and role-gated; read-only roles denied."]
  }),
  coachingRoute("sessions.rescheduleOne", "session-service", "POST", "/sessions/:session_id/reschedule", "coaching-shell-session-routes.ts", 67, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["One-off reschedule preserves the same session id; read-only roles status-only."]
  }),
  coachingRoute("sessions.syncZoom", "session-service", "POST", "/sessions/:session_id/sync-zoom", "coaching-shell-session-routes.ts", 69, {
    sideEffects: ["readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Zoom provider side effect is readiness/evidence until explicitly opened."]
  }),
  coachingRoute("sessions.reassignCoach", "session-service", "POST", "/sessions/:session_id/reassign-coach", "coaching-shell-session-routes.ts", 75, {
    roleScopeNotes: ["Admin/super_admin only; coach cannot reassign session to another coach."]
  }),
  coachingRoute("sessions.lifecycle", "session-service", "POST", "/sessions/:session_id/lifecycle", "coaching-shell-session-routes.ts", 70, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["completed|cancelled|archived transition; assigned-session scope; read-only roles denied."]
  }),

  coachingRoute("reports.list", "artifact-service", "GET", "/reports", "coaching-shell-report-routes.ts", 162),
  coachingRoute("reports.sessionReport", "artifact-service", "GET", "/sessions/:session_id/report", "coaching-shell-report-routes.ts", 163, {
    roleScopeNotes: ["participant may access own reports only; generic not-found for out-of-scope artifacts."]
  }),
  coachingRoute("reports.sessionReportDownload", "artifact-service", "GET", "/sessions/:session_id/report/download", "coaching-shell-report-routes.ts", 164, {
    confidence: "catalog-source-backed",
    roleScopeNotes: ["Returns a self-contained HTML attachment after the backend re-applies report visibility, scope, and download policy."],
    notes: "Binary report download; optional reportId query selects the opened report within scope and filename is supplied by Content-Disposition."
  }),
  coachingRoute("reports.sessionTranscript", "artifact-service", "GET", "/sessions/:session_id/transcript", "coaching-shell-report-routes.ts", 165, {
    roleScopeNotes: ["participant has no transcript access; company_admin transcript content must exclude coach-private content."]
  }),
  // Phase 4 follow-through write-back (report-generation spec §2.3a, org-admin
  // spec §2.3a). decision="reviewed" sets reviewed_at; approved_at stays
  // staff-only. Backend guard re-applies the D-3 predicate (a company_admin can
  // never review vjal_internal/ceo_confidential -> 403). UUID Idempotency-Key.
  coachingRoute("reports.markReviewed", "artifact-service", "POST", "/reports/:report_id/review", "coaching-shell-report-routes.ts", 180, {
    idempotency: "required",
    confidence: "source-provisional",
    sideEffects: ["db1-write", "readiness-evidence"],
    roleScopeNotes: [
      "company_admin/coach may review team_admin/company_readable reports in scope; never settable on vjal_internal/ceo_confidential.",
      "approved_at is staff-only and never settable by company_admin or cohort_lead."
    ],
    notes: "Phase-3 mark-reviewed write route (landing now); fail-closed CO_PERMISSION_DENIED out of scope/wrong report_type."
  }),
  // Phase 4 CSV export (org-admin spec §2.3b) — reuses GET /exports/:export_type.
  // The reports/transcripts branch applies the D-3 predicate (G3): a
  // company_admin export of reports returns only team_admin/company_readable.
  // Returns text/csv, not JSON (handled via api-client text path).
  coachingRoute("exports.csv", "export-service", "GET", "/exports/:export_type", "coaching-shell-export-routes.ts", 284, {
    roleScopeNotes: [
      "super_admin/admin/coach/company_admin may export; participant/cohort_lead denied bulk CSV.",
      "reports/transcripts export branch is D-3-filtered (G3): never confidential/coach/internal for company_admin."
    ],
    notes: "Existing export handler; the report branch must be D-3-filtered before the UI gate flips. text/csv response."
  }),
  // Phase 4 org analytics rollup (analytics spec §4.4) — ungated reads; HTTP 200
  // zero-filled when no ingested data, NEVER 503. Empty by data absence.
  coachingRoute("analytics.orgSummary", "analytics-service", "GET", "/analytics/org-summary", "coaching-shell-analytics-routes.ts", 1, {
    confidence: "source-provisional",
    roleScopeNotes: [
      "view.dashboard auth; company_admin resolves to mode:company; report-status aggregates are D-3-filtered.",
      "ungated read; zero-filled HTTP 200 when no ingested data, never 503."
    ],
    notes: "Phase-3 analytics rollup (landing now); aggregates + counts only, no raw provider payloads."
  }),
  coachingRoute("analytics.timeSeries", "analytics-service", "GET", "/analytics/time-series", "coaching-shell-analytics-routes.ts", 2, {
    confidence: "source-provisional",
    roleScopeNotes: ["week-ordered attendance/submission/completion points; company-scoped; ungated zero-filled never 503."],
    notes: "Phase-3 analytics rollup (landing now)."
  }),
  coachingRoute("analytics.reportStatus", "analytics-service", "GET", "/analytics/report-status", "coaching-shell-analytics-routes.ts", 3, {
    confidence: "source-provisional",
    roleScopeNotes: [
      "report-row status rollup across cohorts; D-3-filtered (company_admin byType excludes vjal_internal/ceo_confidential).",
      "ungated zero-filled never 503."
    ],
    notes: "Phase-3 analytics rollup (landing now)."
  }),

  // ── Readiness lane: NOW-LIVE settings / capstone-read / certificate /
  // report-generate backend slices (vjal-coaching-api FR-SET / F15 / FR-CERT /
  // FR-REPORT). All camelCase, STRING ids, bare-array reads, ?orgId injected by
  // the shared transport, UUID Idempotency-Key on writes, CO_* envelope. The
  // surfaces render the typed-honest backend states verbatim (artifactRef null =
  // pending; report null = pending/queued — never fabricated).
  coachingRoute("settings.thresholds.list", "settings-service", "GET", "/settings/thresholds", "coaching-shell-settings-routes.ts", 87, {
    roleScopeNotes: ["view.settings: staff/coach-where-allowed read; participant denied (admin surface)."]
  }),
  coachingRoute("settings.thresholds.upsert", "settings-service", "POST", "/settings/thresholds", "coaching-shell-settings-routes.ts", 89, {
    roleScopeNotes: ["manage.settings = super_admin/admin only; coach/company_admin/participant denied the write."]
  }),

  coachingRoute("capstone.submissions.listForCohort", "capstone-service", "GET", "/cohorts/:cohort_id/capstone-submissions", "coaching-shell-capstone-routes.ts", 120, {
    roleScopeNotes: ["coaching.reports.view.org + discriminated read scope; a PARTICIPANT sees ONLY their own submission(s)."]
  }),
  coachingRoute("capstone.submissions.detail", "capstone-service", "GET", "/capstone-submissions/:submission_id", "coaching-shell-capstone-routes.ts", 121, {
    roleScopeNotes: ["Own-scope only for a participant; a foreign submission id 404s-via-400 'was not found'. reportId is a reference only."]
  }),

  coachingRoute("certificates.list", "certificate-service", "GET", "/certificates", "coaching-shell-certificate-routes.ts", 161, {
    roleScopeNotes: ["view.certificates + discriminated read scope; a PARTICIPANT sees ONLY their own certificate(s). artifactRef null = pending."]
  }),
  coachingRoute("certificates.detail", "certificate-service", "GET", "/certificates/:certificate_id", "coaching-shell-certificate-routes.ts", 162, {
    roleScopeNotes: ["Own-scope only for a participant; a foreign certificate id 404s-via-400."]
  }),
  coachingRoute("certificates.issue", "certificate-service", "POST", "/certificates", "coaching-shell-certificate-routes.ts", 163, {
    sideEffects: ["db1-write", "readiness-evidence"],
    roleScopeNotes: ["issue.certificates = super_admin/admin only (migration 0224); artifactRef stays null (pending), never a fabricated URL."]
  }),

  coachingRoute("reports.generate", "artifact-service", "POST", "/sessions/:session_id/reports/generate", "coaching-shell-report-routes.ts", 476, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: [
      "generate.reports = super_admin/admin/coach-in-scope; a foreign session 404s-via-400.",
      "report:null + status:'pending' while the report-agent lane is closed (dry_run); NEVER a fabricated 'generated'."
    ]
  }),
  coachingRoute("reports.regenerate", "artifact-service", "POST", "/sessions/:session_id/reports/regenerate", "coaching-shell-report-routes.ts", 477, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: [
      "regenerate.reports = super_admin ONLY (migration 0225 / FR-REPORT-003); all other roles denied.",
      "report:null + status:'pending' while the lane is closed; NEVER a fabricated 'generated'."
    ]
  }),

  // FR-SESS-002 Clear Series ("KEEP + DETACH") — NOW LIVE. SA may clear any
  // cohort's series; a coach only a cohort assigned to them (an out-of-scope/
  // unknown cohort 404s-via-400 so a coach cannot probe). Requires { confirm:true }
  // + a UUID Idempotency-Key. It clears FUTURE sessions and KEEPS-AND-DETACHES the
  // invites/recordings/reports (never deletes them). Idempotent on the key.
  coachingRoute("sessions.clearSeries", "session-service", "POST", "/cohorts/:cohort_id/sessions/clear", "coaching-shell-session-clear-series-route.ts", 32, {
    sideEffects: ["db1-write", "readiness-evidence"],
    roleScopeNotes: [
      "coaching.sessions.clear-series.org = super_admin + coach only (migration 0227); admin/company_admin/cohort_lead/participant denied.",
      "Destructive confirm required; KEEP + DETACH semantics (invites/recordings/reports kept, never deleted)."
    ]
  }),

  // FR-TOOL-001 Tool View diagnostics — NOW LIVE. SUPER-ADMIN ONLY, READ-ONLY.
  // Returns provider-gate states + outbox health counts + non-secret config flags.
  // NO SECRET VALUES: every secret-bearing var is a configured/not-configured
  // boolean only (secretsRedacted:true). admin/coach/participant denied.
  coachingRoute("tools.diagnostics", "tool-view-service", "GET", "/tools/diagnostics", "coaching-shell-tool-view-routes.ts", 32, {
    roleScopeNotes: [
      "coaching.tools.view.org = super_admin ONLY (migration 0227) + defense-in-depth role==='super_admin' check; everyone else denied.",
      "Read-only; the response carries NO secret values (booleans/states/counts only)."
    ]
  }),

  // Z6 Zoom-integration read/action surfaces (F1/F2/F4/F5/F8/F9/F10/F14),
  // consuming the Z1-Z5 backend routes. All reads are role-scoped server-side;
  // mutations require a UUID Idempotency-Key. Provider execution remains
  // fail-closed behind the coaching gates and is surfaced as dry_run/queued.
  coachingRoute("sessions.recordings", "session-service", "GET", "/sessions/:session_id/recordings", "coaching-shell-session-integration-routes.ts", 134, {
    roleScopeNotes: ["F1: role-scoped recording read; secure link behind auth, passcode presence only, raw passcode never returned."]
  }),
  coachingRoute("sessions.inviteLog", "session-service", "GET", "/sessions/:session_id/invite-log", "coaching-shell-session-integration-routes.ts", 106, {
    roleScopeNotes: ["F5: append-only per-attempt invite trigger log; no provider payloads, emails, join URLs, or passcodes."]
  }),
  coachingRoute("sessions.postSessionEmail", "session-service", "POST", "/sessions/:session_id/post-session-email", "coaching-shell-z5-routes.ts", 259, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["F9: enqueues one email intent + one recipient row per active participant; delivery fail-closed until COACHING_EMAIL_LIVE_ENABLED."]
  }),
  coachingRoute("sessions.postSessionEmailLog", "session-service", "GET", "/sessions/:session_id/post-session-email-log", "coaching-shell-z5-routes.ts", 260, {
    roleScopeNotes: ["F9: per-session post-session email requests + per-recipient delivery rollups."]
  }),
  coachingRoute("cohorts.progressWeekWise", "cohort-service", "GET", "/cohorts/:cohort_id/progress/week-wise", "coaching-shell-z5-routes.ts", 256, {
    roleScopeNotes: ["F2: per-week per-participant progress cells; out-of-scope cohort resolves as not-found."]
  }),
  coachingRoute("cohorts.attendance", "cohort-service", "GET", "/cohorts/:cohort_id/attendance", "coaching-shell-z5-routes.ts", 257, {
    roleScopeNotes: ["F8: per-week attendance rollup over the Z4-ingested attendance_status; ingested flag honest when no data."]
  }),
  coachingRoute("cohorts.assignments", "cohort-service", "GET", "/cohorts/:cohort_id/assignments", "coaching-shell-z5-routes.ts", 258, {
    roleScopeNotes: ["F10: per-week Jotform assignment submission log; unmatched submissions surfaced honestly."]
  }),
  coachingRoute("cohorts.reassignCoach", "cohort-service", "POST", "/cohorts/:cohort_id/reassign-coach", "coaching-shell-session-integration-routes.ts", 207, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["F13: admin-only cohort reassign; future-session host transfer fans out via the outbox, past sessions/recordings preserved."]
  }),
  coachingRoute("participants.removeFromCohort", "participant-service", "DELETE", "/cohorts/:cohort_id/participants/:participant_id", "coaching-shell-session-participant-routes.ts", 231, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["F6: admin removes a participant from a cohort; future registrants + calendar events are cancelled via the outbox."]
  }),
  coachingRoute("useCases.list", "use-case-service", "GET", "/use-cases", "coaching-shell-z5-routes.ts", 261, {
    roleScopeNotes: ["F14: org-scoped use-case repository list; visible where cohorts are listed."]
  }),
  coachingRoute("useCases.create", "use-case-service", "POST", "/use-cases", "coaching-shell-z5-routes.ts", 262, {
    roleScopeNotes: ["F14: staff/coach authoring only; company/participant/cohort_lead cannot create."]
  }),
  coachingRoute("useCases.detail", "use-case-service", "GET", "/use-cases/:use_case_id", "coaching-shell-z5-routes.ts", 263, {
    roleScopeNotes: ["F14: org-scoped single use-case read."]
  }),

  coachingRoute("resources.list", "resource-service", "GET", "/resources", "coaching-shell-resource-routes.ts", 233),
  coachingRoute("resources.detail", "resource-service", "GET", "/resources/:resource_id", "coaching-shell-resource-routes.ts", 234),
  coachingRoute("resources.create", "resource-service", "POST", "/resources", "coaching-shell-resource-routes.ts", 235, {
    roleScopeNotes: ["admin/super_admin manage resources; coach/company_admin/participant read-only."]
  }),
  coachingRoute("resources.update", "resource-service", "PATCH", "/resources/:resource_id", "coaching-shell-resource-routes.ts", 236, {
    roleScopeNotes: ["admin/super_admin manage resources; read-only roles denied."]
  }),
  coachingRoute("resources.completeUpload", "resource-service", "POST", "/resources/:resource_id/uploads/:upload_id/complete", "coaching-shell-resource-routes.ts", 237, {
    sideEffects: ["db1-write", "readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Uploads/R2 side effects remain readiness-gated until explicitly opened."]
  }),
  coachingRoute("resources.requestDownload", "resource-service", "POST", "/resources/:resource_id/uploads/:upload_id/download", "coaching-shell-resource-routes.ts", 240, {
    sideEffects: ["readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Download authorization is role-scoped; global resources visible to company_admin and participant."]
  }),
  coachingRoute("resources.deleteUpload", "resource-service", "DELETE", "/resources/:resource_id/uploads/:upload_id", "coaching-shell-resource-routes.ts", 243, {
    roleScopeNotes: ["Destructive upload action is admin/super_admin only and must be confirmed in UI."]
  }),

  coachingRoute("history.list", "history-service", "GET", "/event-history", "coaching-shell-observability-routes.ts", 281),
  coachingRoute("history.detail", "history-service", "GET", "/event-history/:event_id", "coaching-shell-observability-routes.ts", 282, {
    roleScopeNotes: ["participant has no operational history; coach/company_admin see human-readable scoped summaries."]
  }),
  coachingRoute("users.list", "user-service", "GET", "/users", "coaching-shell-observability-routes.ts", 285),
  coachingRoute("users.assignableCoaches", "user-service", "GET", "/users/assignable-coaches", "coaching-shell-observability-routes.ts", 468, {
    roleScopeNotes: ["Coach callers receive only active coaching-coach selector rows in their requested organization; no global user administration fields."]
  }),
  coachingRoute("users.create", "user-service", "POST", "/users", "coaching-shell-observability-routes.ts", 286, {
    roleScopeNotes: ["admin can create coach users; super_admin owns elevated role grants."]
  }),
  coachingRoute("users.update", "user-service", "PATCH", "/users/:user_id", "coaching-shell-observability-routes.ts", 287, {
    roleScopeNotes: ["admin cannot grant admin/super_admin; profile fields are role-restricted."]
  }),
  coachingRoute("profile.update", "user-service", "PATCH", "/profile", "coaching-shell-observability-routes.ts", 284, {
    confidence: "catalog-needs-annotation",
    catalogStableKey: "vjal-coaching-api:PATCH:/api/coaching/profile",
    roleScopeNotes: [
      "All roles may update allowed self-service fields only; email, credentials, billing, and permissions are locked handoffs.",
      "Participant profile updates cannot set title (403 from updateOwnProfileRoute)."
    ],
    notes: "Source-confirmed self-service profile route; requires orgId query plus a UUID Idempotency-Key header."
  }),
  coachingRoute("users.deactivate", "user-service", "POST", "/users/:user_id/deactivate", "coaching-shell-observability-routes.ts", 288, {
    roleScopeNotes: ["Deactivate/reactivate preferred over permanent delete; assigned active cohort blocker may apply."]
  }),
  coachingRoute("users.zoomStatus", "user-service", "GET", "/users/:user_id/zoom-status", "coaching-shell-observability-routes.ts", 289, {
    roleScopeNotes: ["Credential readiness is visible; product users do not manage raw provider credentials."]
  }),

  coachingRoute("platformOrganizations.create", "platform-organizations-service", "POST", "/platform/organizations", "coaching-platform-organizations-shell-routes.ts", 67, {
    idempotency: "required",
    confidence: "source-provisional",
    roleScopeNotes: ["Platform JWT and DB profile is_admin are both required; only platform admins may create organizations."],
    notes: "Transactional Coaching organization creation with honest invite status and copyable sign-in URL."
  }),
  coachingRoute("platformOrganizations.list", "platform-organizations-service", "GET", "/platform/organizations", "coaching-platform-organizations-shell-routes.ts", 95, {
    idempotency: "forbidden",
    confidence: "source-provisional",
    roleScopeNotes: ["Platform JWT and DB profile is_admin are both required; list includes every organization with Coaching grants or data."],
    notes: "Platform organization visibility projection with Coaching counts and caller grant role."
  }),
  coachingRoute("platformEvents.readiness", "platform-events-service", "POST", "/platform-events", "coaching-platform-event-shell-routes.ts", 116, {
    confidence: "catalog-needs-annotation",
    catalogStableKey: "vjal-coaching-api:POST:/api/coaching/platform-events",
    sideEffects: ["readiness-evidence", "provider-closed"],
    roleScopeNotes: ["Browser code may call only a same-origin product proxy; no direct Events API calls."]
  })
] as const satisfies readonly ApiEndpointContract[];

export type CoachingEndpointKey = (typeof contracts)[number]["key"];
export const coachingEndpointContracts: readonly ApiEndpointContract[] = contracts;

export const coachingEndpoints = contracts.reduce(
  (accumulator, contract) => ({
    ...accumulator,
    [contract.key]: contract
  }),
  {} as Record<CoachingEndpointKey, ApiEndpointContract>
);

export { coachingApiContractBlockers };

export function listProvisionalCoachingContracts() {
  return coachingEndpointContracts.filter((contract) => contract.confidence !== "catalog-source-backed");
}

export function listRequiredIdempotencyContracts() {
  return coachingEndpointContracts.filter((contract) => contract.idempotency === "required");
}
